/**
 * PATH: apps/sam/src/features/email/stores/emailFilterStore.ts
 * FILE_ID: SAM-EMAIL-STORE-001
 * PURPOSE: Cross-component email filter state — specifically the
 *          set of MAILBOXES the user wants to see emails from. Shared
 *          between the EmailLayout sidebar footer (toggle UI) and the
 *          InboxPage list (applies the filter). Persisted to
 *          localStorage so the user's choice survives reloads.
 * WHY:     The user explicitly asked: "you should be able to switch
 *          on / off view emails from other mailboxes". A user can own
 *          MORE THAN ONE mailbox (their personal inbox + a shared
 *          ``quotes@`` mailbox), so visibility must be keyed on the
 *          MAILBOX, not the owning user — keying on the user collapsed
 *          both mailboxes into one un-splittable toggle.
 *
 * MAILBOX KEY (the unit of visibility):
 *   - ``a:<accountId>`` — a real EmailAccount (the post-attribution
 *     identity; each connected mailbox is independent).
 *   - ``u:<userId>``    — the LEGACY / "older mail" bucket for rows
 *     synced before per-mailbox attribution existed (no account stamp).
 *   ``mailboxKey()`` derives the key from a {account_id, user_id} shape
 *   and is the single source of truth both surfaces (switcher +
 *   sidebar) and the InboxPage filter-builder agree on.
 *
 * STORAGE FORMAT:
 *   - Storage key: ``email_visible_mailboxes_v2`` (v1 stored bare user
 *     ids; bumping the key resets stale per-user hides to "show all" —
 *     a harmless one-time reset given the opt-out default).
 *   - Format: JSON array of mailbox KEYS, e.g. ``["a:12","u:15"]``.
 *   - Empty array means "show ALL mailboxes" (the default) so a user
 *     who has never toggled — or who connects a new mailbox — isn't
 *     filtered out of their own mail.
 */

import { create } from 'zustand'
import {
  fetchServerState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'

// v2: the hidden set now stores per-MAILBOX keys (``a:<accountId>`` /
// ``u:<userId>``) instead of bare numeric user ids, so the key is bumped
// to drop any stale v1 numeric arrays rather than misread them as keys.
const STORAGE_KEY = 'email_visible_mailboxes_v2'
// Hidden-mailbox choices are an explicit operator preference that should
// follow them across devices, not die with this browser's cache.
// localStorage stays the instant cache; we mirror writes into the durable
// per-user/per-org store and hydrate once on load. Fail-soft.
const HIDDEN_MAILBOXES_DB_SCOPE = 'email:hidden-mailboxes'

/** Shape carrying the two ids a mailbox can be identified by. */
export interface MailboxIdentity {
  account_id?: number | null
  user_id?: number | null
}

/**
 * Derive the stable visibility key for a mailbox. Prefers the real
 * EmailAccount id (positive) so two mailboxes owned by the same user
 * are distinct; falls back to the owning user for the legacy bucket.
 * Returns ``null`` when neither id is usable (row can't be toggled).
 */
export function mailboxKey(m: MailboxIdentity): string | null {
  if (typeof m.account_id === 'number' && m.account_id > 0) {
    return `a:${m.account_id}`
  }
  if (typeof m.user_id === 'number') return `u:${m.user_id}`
  return null
}

/** Split a set of visible mailbox keys into the two backend filter
 *  param lists: account ids (``synced_by_account_id__in``) and legacy
 *  owner ids (``unattributed_owner_id__in``). */
export function splitMailboxKeys(keys: Iterable<string>): {
  accountIds: number[]
  unattributedUserIds: number[]
} {
  const accountIds: number[] = []
  const unattributedUserIds: number[] = []
  for (const k of keys) {
    if (k.startsWith('a:')) {
      const n = Number(k.slice(2))
      if (Number.isFinite(n)) accountIds.push(n)
    } else if (k.startsWith('u:')) {
      const n = Number(k.slice(2))
      if (Number.isFinite(n)) unattributedUserIds.push(n)
    }
  }
  return { accountIds, unattributedUserIds }
}

function loadHidden(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((s) => typeof s === 'string'))
  } catch {
    return new Set()
  }
}

function saveHidden(hidden: Set<string>): void {
  if (typeof window === 'undefined') return
  const arr = Array.from(hidden)
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(arr))
  } catch {
    // Storage quota exceeded / disabled — non-fatal, the toggle
    // simply won't survive reload.
  }
  _dbTouched = true
  scheduleServerSave<{ hidden: string[] }>(HIDDEN_MAILBOXES_DB_SCOPE, { hidden: arr })
}

interface EmailFilterState {
  /** Mailboxes (by mailbox KEY) the viewer has explicitly hidden.
   *  Storing the *hidden* set rather than the visible set means a
   *  newly-connected mailbox is visible by default — opt-out
   *  rather than opt-in. */
  hiddenMailboxes: Set<string>
  /** Toggle a mailbox's visibility (by mailbox key). */
  toggleMailbox: (key: string) => void
  /** Show every connected mailbox (clears the hidden set). */
  showAllMailboxes: () => void
  /** Hide every mailbox except one (used by the right-click "Solo"
   *  option in the EmailLayout footer — focus on a single mailbox
   *  while everyone else's mail recedes). */
  soloMailbox: (key: string, allKeys: string[]) => void
  /** Read-only helper: return the visible-key list given the full set
   *  of connected mailbox keys. Empty input ⇒ empty result. */
  visibleKeys: (allKeys: string[]) => string[]
}

export const useEmailFilterStore = create<EmailFilterState>((set, get) => ({
  hiddenMailboxes: loadHidden(),

  toggleMailbox: (key) => {
    set((s) => {
      const next = new Set(s.hiddenMailboxes)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      saveHidden(next)
      return { hiddenMailboxes: next }
    })
  },

  showAllMailboxes: () => {
    saveHidden(new Set())
    set({ hiddenMailboxes: new Set() })
  },

  soloMailbox: (key, allKeys) => {
    const next = new Set(allKeys.filter((k) => k !== key))
    saveHidden(next)
    set({ hiddenMailboxes: next })
  },

  visibleKeys: (allKeys) => {
    const hidden = get().hiddenMailboxes
    return allKeys.filter((k) => !hidden.has(k))
  },
}))

// ─── Durable (cross-device) hydration ────────────────────────────────
// localStorage gates first paint above; here we pull the durable server
// copy ONCE and apply it unless the operator already toggled a mailbox
// this session (their fresh choice is already being debounce-saved up).
let _dbTouched = false
// BUG-2026-07-17-EMAIL-ORG-SWITCH-STALE-MAILBOX — the server copy of
// the hidden set is per-user/PER-ORG (includeTenant), but this module
// hydrated it exactly once per page load, so switching organisation
// kept the previous org's hidden mailbox keys in memory + localStorage.
// EmailLayout calls ``rehydrateHiddenMailboxes(orgId)`` whenever the
// session org changes; a changed org resets the "touched" latch (a
// toggle made under the OLD org must not veto the NEW org's server
// state) and replaces the store wholesale.
let _lastHydratedOrg: number | null | undefined
let _hydrationGeneration = 0
async function hydrateFromServer(
  generation: number,
  orgId: number | null,
): Promise<void> {
  const s = await fetchServerState<{ hidden: string[] }>(
    HIDDEN_MAILBOXES_DB_SCOPE, { hidden: [] },
  )
  if (
    generation !== _hydrationGeneration
    || orgId !== _lastHydratedOrg
    || _dbTouched
  ) return
  const next = new Set(
    s && Array.isArray(s.hidden)
      ? s.hidden.filter((k) => typeof k === 'string')
      : [],
  )
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next))) } catch { /* ignore */ }
  useEmailFilterStore.setState({ hiddenMailboxes: next })
}

export function rehydrateHiddenMailboxes(orgId: number | null): void {
  if (typeof window === 'undefined') return
  if (_lastHydratedOrg !== undefined && _lastHydratedOrg === orgId) return
  const isSwitch = _lastHydratedOrg !== undefined
  _lastHydratedOrg = orgId
  _hydrationGeneration += 1
  if (isSwitch) {
    _dbTouched = false
    useEmailFilterStore.setState({ hiddenMailboxes: new Set() })
    try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  }
  void hydrateFromServer(_hydrationGeneration, orgId)
}
